import { useMemo, useState } from 'react';
import { Box, FormControlLabel, makeStyles } from '@material-ui/core';
import { SBase } from '@setvi/shared/components';
import { SCheckbox } from '@setvi/shared/components/scheckbox';
import { Customer } from 'Services/Mongo/Types';
import { CustomerDropdown } from 'componentsV2/customer-dropdown';
import {
  CatalogCustomerViewOptions,
  ProductColumnsIds,
  ProductDetailsTabsIds
} from '@setvi/shared/interfaces';
import { useCatalogConfig } from '../hooks/useCatalogConfig';
import { InvalidConfiguration } from '../components';
import {
  buildCustomerViewUrl,
  type CustomerViewSearchConfig
} from '../customer-view-page';

const useStyles = makeStyles(({ spacing }) => ({
  option: {
    '& span.MuiTypography-body1': {
      marginLeft: spacing(2)
    }
  }
}));

const HIDDEN_TABS = new Set<string>([
  ProductDetailsTabsIds.SALES_ENABLEMENT,
  ProductDetailsTabsIds.PRODUCT_ENHANCER
]);

interface CustomerViewProps {
  onClose: () => void;
}

export const CustomerView = ({ onClose }: CustomerViewProps) => {
  const [selectedCustomer, setSelectedCustomer] = useState<Customer>(null);
  const { configuration, invalidConfiguration } = useCatalogConfig();
  const classes = useStyles();

  const optionalColumns = useMemo(
    () =>
      configuration?.product?.columns?.filter(
        col => col.customerView?.hide === CatalogCustomerViewOptions.Optional
      ) || [],
    [configuration?.product?.columns]
  );

  const [checkedColumns, setCheckedColumns] = useState<Record<string, boolean>>(
    () =>
      optionalColumns.reduce(
        (acc, col) => ({ ...acc, [col.id]: true }),
        {} as Record<string, boolean>
      )
  );

  const handleToggle = (id: string, checked: boolean) => {
    setCheckedColumns(prev => ({ ...prev, [id]: checked }));
  };

  const buildConfig = (): CustomerViewSearchConfig => {
    const columns = configuration?.product?.columns || [];
    const visibleColumnIds = columns
      .filter(col => {
        if (col.customerView?.hide === CatalogCustomerViewOptions.Required)
          return false;
        if (col.customerView?.hide === CatalogCustomerViewOptions.Optional)
          return checkedColumns[col.id] ?? true;
        return true;
      })
      .map(col => col.id);

    const visibleSet = new Set(visibleColumnIds);

    const hiddenTabs = new Set<string>(HIDDEN_TABS);
    if (!visibleSet.has(ProductColumnsIds.Stock))
      hiddenTabs.add(ProductDetailsTabsIds.WAREHOUSE);

    return {
      customerAccountNumber: selectedCustomer.accountNumber,
      filters: { customer: { show: false } },
      product: { columns: visibleColumnIds },
      productDetails: {
        pricing: (configuration?.productDetails?.pricing || [])
          .filter(p => visibleSet.has(p.id))
          .map(p => p.id),
        tabs: (configuration?.productDetails?.tabs || [])
          .filter(tab => !hiddenTabs.has(tab.id))
          .map(tab => tab.id)
      }
    };
  };

  if (invalidConfiguration) return <InvalidConfiguration />;

  return (
    <SBase
      width={600}
      disablePadding
      header={{
        title: 'Customer View',
        description:
          'Configure the catalog view for presenting products to your customer.'
      }}
      footer={{
        onConfirm: () => {
          onClose();
          window.open(buildCustomerViewUrl(buildConfig()), '_blank');
        },
        onClose,
        confirmLabel: 'View',
        closeLabel: 'Cancel',
        startIcon: null,
        disableConfirm: !selectedCustomer,
        divider: false
      }}>
      <Box display="flex" flexDirection="column" p={2} mx={4}>
        <CustomerDropdown
          label="Customer"
          required
          style={{
            input: { width: '100%' },
            dropdownWrapperWidth: 511
          }}
          selectedCustomer={selectedCustomer}
          onSelectCustomer={setSelectedCustomer}
          placeholder="Select customer"
        />
        {optionalColumns.length > 0 && (
          <Box display="flex" flexDirection="column" gridGap="8px" p={2} pt={3}>
            {optionalColumns.map(column => (
              <FormControlLabel
                className={classes.option}
                key={column.id}
                control={
                  <SCheckbox
                    checked={checkedColumns[column.id] ?? true}
                    onChange={(_, checked) => handleToggle(column.id, checked)}
                  />
                }
                label={`Show ${column.customerView?.optionLabel || column.label}`}
              />
            ))}
          </Box>
        )}
      </Box>
    </SBase>
  );
};

export default CustomerView;
